import {
  Badge,
  Button,
  DataTable,
  DisableableAction,
  LegacyCard,
  LegacyStack,
  Text,
} from '@shopify/polaris'
import { Tone } from '@shopify/polaris/build/ts/src/components/Badge'
import { CaretDownIcon, CaretUpIcon } from '@shopify/polaris-icons'
import React from 'react'
import { mutate } from 'swr'

import { SubscriptionContractSubscriptionStatus } from '../common/api'
import { ApiActionDialog } from '../common/ApiActionDialog'
import { ShortDate, useDialogActivator, useToggle } from '../common/helpers'
import { BillingAttempt, SubscriptionContract } from '../common/subscription-models'
import { PaymentDetails } from './PaymentDetails'

const statusBadgeTone = (ba: BillingAttempt): Tone =>
  ba.status === 'Pending' ? 'new' : ba.status === 'Success' ? 'success' : 'critical'

const StatusBadge = ({ attempt }: { attempt: BillingAttempt }) => (
  <Badge tone={statusBadgeTone(attempt)}>{attempt.status}</Badge>
)

const billingAttemptForFirstOrder = (sc: SubscriptionContract): BillingAttempt => ({
  ready: 1,
  created_at: sc.gql_data.createdAt,
  error_message: undefined,
  status: 'Success',
  gql_data: {
    errorCode: undefined,
    order: sc.origin_order,
  },
})

export const PaymentsCard = ({ sc }: { sc: SubscriptionContract }) => {
  const chargeNowDialogActivator = useDialogActivator()
  const unpaidOrderDialogActivator = useDialogActivator()
  const canChargeNow =
    sc.is_editable &&
    (SubscriptionContractSubscriptionStatus.Active === sc.gql_status ||
      SubscriptionContractSubscriptionStatus.Paused === sc.gql_status)

  const attempts: BillingAttempt[] = [...sc.billing_attempts]
  if (sc.origin_order) {
    attempts.push(billingAttemptForFirstOrder(sc))
  }

  const [expanded, toggleExpanded] = useToggle(false)
  const visibleAttempts = expanded ? attempts : attempts.slice(0, 5)
  const rows = visibleAttempts.map((ba: BillingAttempt) => [
    <ShortDate date={ba.created_at} noWrap />,
    <StatusBadge attempt={ba} />,
    <PaymentDetails
      subscriptionRestId={sc.rest_id}
      baId={ba.id}
      status={ba.status}
      errorCode={ba.gql_data?.errorCode}
      errorMessage={ba.error_message}
      orderGid={ba.gql_data?.order?.id}
      orderName={ba.gql_data?.order?.name}
      nextActionUrl={ba.gql_data?.nextActionUrl}
    />,
  ])

  let actions: DisableableAction[] = []

  if (canChargeNow) {
    actions.push({ content: 'Charge now', onAction: chargeNowDialogActivator.show })
  }

  if (canChargeNow && !sc.gql_data.customerPaymentMethod) {
    actions.push({ content: 'Create unpaid order', onAction: unpaidOrderDialogActivator.show })
  }

  return (
    <LegacyCard title="Payments" actions={actions}>
      {attempts.length === 0 ? (
        <LegacyCard.Section>
          <Text as="p" tone="subdued">
            No payments have been attempted for this subscription
          </Text>
        </LegacyCard.Section>
      ) : (
        <>
          <DataTable
            columnContentTypes={['text', 'text', 'text']}
            headings={['Date', 'Status', 'Details']}
            rows={rows}
          />
        </>
      )}
      <ApiActionDialog
        activator={chargeNowDialogActivator}
        url={`/subscriptions/${sc.rest_id}/attempt-billing`}
        method="POST"
        message="Are you sure to charge now for the next order?"
        primaryButtonTitle="Charge"
        onSuccess={(res) => mutate(['subscription-contract', `${sc.rest_id}`], res.data, false)}
      />
      <ApiActionDialog
        activator={unpaidOrderDialogActivator}
        url={`/subscriptions/${sc.rest_id}/attempt-billing`}
        method="POST"
        opts={{ body: JSON.stringify({ createUnpaidOrder: true }) }}
        title="Create unpaid order"
        message="The next order will be created in Shopify and marked as unpaid, without charging the customer. Do you want to continue?"
        primaryButtonTitle="Create order"
        cancelButtonTitle="Cancel"
        onSuccess={(res) => mutate(['subscription-contract', `${sc.rest_id}`], res.data, false)}
      />

      <LegacyCard.Section subdued>
        <LegacyStack distribution="equalSpacing">
          <span>
            {attempts.length > 5 && (
              <Button
                onClick={toggleExpanded}
                icon={expanded ? CaretUpIcon : CaretDownIcon}
                variant="plain"
              >
                {expanded ? 'Hide all' : `View all (${attempts.length})`}
              </Button>
            )}
          </span>
          <Text as="span" tone="subdued">
            Successful: {sc.processed_installments}
            {sc.remaining_installments !== null && `, Remaining: ${sc.remaining_installments}`}
          </Text>
        </LegacyStack>
      </LegacyCard.Section>
    </LegacyCard>
  )
}
